import { useRef, type MouseEvent } from "react";
import { Plus, Star } from "lucide-react";
import { usePage, type SectionProps } from "@/flash/context";
import { A, arr, cn, rv, safeHref, str, tel } from "@/flash/ui";
import { Kicker, MaskTitle, Pic, Shell } from "./kit";

/* ───────── 06 · Trust ───────── */

export function SysTestimonials({ title, score, summary, items }: SectionProps) {
  const list = arr(items).slice(0, 5);
  const [lead, ...rest] = list;
  const cite = (t: Record<string, unknown>, big?: boolean) => (
    <figcaption className={cn("flex flex-col gap-0.5", big ? "mt-8" : "mt-6")}>
      <cite className="text-[14px] font-medium not-italic text-foreground">{str(t.name)}</cite>
      {t.role ? <span className="text-[13px] text-muted-foreground">{str(t.role)}</span> : null}
    </figcaption>
  );
  return (
    <section id="reviews" className="py-24 md:py-36">
      <Shell>
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-8">
          <div {...rv()} className="flex flex-col lg:col-span-4">
            <Kicker>Trust</Kicker>
            <h2 className="sx-display-sm mt-6 max-w-xs">{str(title)}</h2>
            {str(score) ? (
              <div className="mt-12 flex items-end gap-5 border-t border-white/[.08] pt-8">
                <span className="font-display text-[5.5rem] font-normal leading-[.8] tracking-[-0.05em] tabular-nums">{str(score)}</span>
                <div className="flex flex-col gap-2 pb-1">
                  <span className="flex gap-0.5 text-primary" role="img" aria-label={`${str(score)} / 5`}>
                    {Array.from({ length: 5 }, (_, i) => (
                      <Star key={i} className="size-3.5 fill-current" strokeWidth={0} />
                    ))}
                  </span>
                  {summary ? <span className="text-[13px] text-muted-foreground">{str(summary)}</span> : null}
                </div>
              </div>
            ) : null}
          </div>
          <div className="flex flex-col lg:col-span-8">
            {lead ? (
              <figure {...rv(1)} className="flex flex-col">
                <blockquote className="font-display text-[clamp(1.6rem,3vw,2.5rem)] leading-[1.18] tracking-[-0.025em] text-foreground">&ldquo;{str(lead.quote)}&rdquo;</blockquote>
                {cite(lead, true)}
              </figure>
            ) : null}
            <div className="mt-16 grid gap-x-10 md:grid-cols-2">
              {rest.map((t, i) => (
                <figure key={i} {...rv(i)} className="flex flex-col border-t border-white/[.08] py-8">
                  <blockquote className="text-[15px] leading-relaxed text-foreground/75">{str(t.quote)}</blockquote>
                  {cite(t)}
                </figure>
              ))}
            </div>
          </div>
        </div>
      </Shell>
    </section>
  );
}

/* ───────── FAQ ───────── */

const FAQ_EASE = "cubic-bezier(.2, .7, .2, 1)";

/** Native <details> kept for semantics/keyboard; only the height change is animated. */
function FaqItem({ q, a }: { q: string; a: string }) {
  const ref = useRef<HTMLDetailsElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const anim = useRef<Animation | null>(null);
  const onToggle = (e: MouseEvent) => {
    const d = ref.current;
    const body = bodyRef.current;
    if (!d || !body || typeof body.animate !== "function") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    e.preventDefault();
    const from = body.getBoundingClientRect().height;
    anim.current?.cancel();
    const closing = d.open && d.dataset.closing !== "1";
    if (closing) {
      d.dataset.closing = "1";
      d.classList.add("is-closing");
      anim.current = body.animate([{ height: `${from}px` }, { height: "0px" }], { duration: 320, easing: FAQ_EASE });
      anim.current.onfinish = () => {
        d.open = false;
        d.classList.remove("is-closing");
        delete d.dataset.closing;
        anim.current = null;
      };
    } else {
      delete d.dataset.closing;
      d.classList.remove("is-closing");
      d.open = true;
      const to = body.scrollHeight;
      anim.current = body.animate([{ height: `${d.open && from ? from : 0}px` }, { height: `${to}px` }], { duration: 360, easing: FAQ_EASE });
      anim.current.onfinish = () => {
        anim.current = null;
      };
    }
  };
  return (
    <details ref={ref} className="sx-faq group border-b border-white/10">
      <summary onClick={onToggle} className="sx-faq-q flex cursor-pointer items-center gap-6 py-6 md:py-7">
        <span className="flex-1 text-[17px] leading-snug text-foreground/90 transition-colors duration-200 group-hover:text-foreground md:text-lg">{q}</span>
        <Plus className="sx-faq-icon size-4 flex-none text-muted-foreground" strokeWidth={1.5} aria-hidden />
      </summary>
      <div ref={bodyRef} className="overflow-hidden">
        <p className="sx-faq-a max-w-2xl pb-7 text-[15px] leading-relaxed text-muted-foreground">{a}</p>
      </div>
    </details>
  );
}

export function SysFaq({ title, items }: SectionProps) {
  return (
    <section id="faq" className="border-t border-white/[.06] py-24 md:py-32">
      <Shell className="grid gap-10 lg:grid-cols-12 lg:gap-8">
        <div className="flex flex-col gap-6 lg:col-span-4">
          <Kicker>FAQ</Kicker>
          <MaskTitle className="sx-display-sm max-w-xs">{str(title)}</MaskTitle>
        </div>
        <div className="border-t border-white/10 lg:col-span-8">
          {arr(items).slice(0, 8).map((q, i) => (
            <FaqItem key={i} q={str(q.question)} a={str(q.answer)} />
          ))}
        </div>
      </Shell>
    </section>
  );
}

/* ───────── 07 · Closing statement ───────── */

export function SysCta({ title, subtitle, button }: SectionProps) {
  const { ctaHref, images, phone } = usePage();
  const call = tel(phone);
  return (
    <section className="relative isolate overflow-hidden pb-16 pt-32 md:pb-24 md:pt-48">
      <Pic img={images.closing} reveal={false} className="absolute inset-0 -z-20" imgClass="object-[center_70%]" sizes="100vw" />
      <div className="sx-closing-glow pointer-events-none absolute inset-0 -z-10" aria-hidden />
      <div className="sx-closing-veil absolute inset-0 -z-10" aria-hidden />
      <Shell className="flex flex-col items-center text-center">
        <MaskTitle className="sx-display-xl max-w-5xl">{str(title)}</MaskTitle>
        {subtitle ? (
          <p {...rv(1)} className="mt-7 max-w-lg text-[16px] leading-relaxed text-foreground/70">
            {str(subtitle)}
          </p>
        ) : null}
        <div {...rv(2)} className="mt-10 flex flex-wrap items-center justify-center gap-3">
          {button ? (
            <A to={ctaHref} className="sx-btn-solid h-11 px-6 text-[14px]">
              {str(button)}
            </A>
          ) : null}
          {call ? (
            <a href={call} className="sx-btn-outline h-11 px-6 text-[14px] tabular-nums">
              {str(phone)}
            </a>
          ) : null}
        </div>
      </Shell>
    </section>
  );
}

/* ───────── Footer ───────── */

export function SysFooter({ brand, tagline, links }: SectionProps) {
  const { phone } = usePage();
  const call = tel(phone);
  const year = new Date().getFullYear();
  return (
    <footer className="flash-footer">
      <Shell className="flex flex-col gap-10 py-14 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-2">
          <strong className="font-display ft-strong text-[17px] font-medium tracking-[-0.02em]">{str(brand)}</strong>
          {tagline ? <p className="ft-muted max-w-xs text-[14px]">{str(tagline)}</p> : null}
        </div>
        <nav className="flex flex-wrap gap-x-10 gap-y-3" aria-label="Footer">
          {arr(links).slice(0, 10).map((l, i) => (
            <A key={i} to={safeHref(l.href)} className="ft-link whitespace-nowrap text-[14px] transition-colors">
              {str(l.label)}
            </A>
          ))}
          {call ? (
            <a href={call} className="ft-link whitespace-nowrap text-[14px] tabular-nums transition-colors">
              {str(phone)}
            </a>
          ) : null}
        </nav>
      </Shell>
      <Shell className="ft-line flex flex-col gap-2 border-t py-6 text-[13px] sm:flex-row sm:justify-between">
        <span className="ft-muted">
          © {year} {str(brand)}
        </span>
        <span className="ft-muted">Order from complexity.</span>
      </Shell>
    </footer>
  );
}